/**
 * Encodes a value for use in a cookie.
 * @param {string} value - The value to encode.
 * @returns {string} - The encoded value.
 */
function write(value) {
  return encodeURIComponent(JSON.stringify(value));
}

/**
 * Retrieves the value of a cookie by name and decodes it.
 * @param {string} name - The name of the cookie.
 * @returns {Object} - The decoded and parsed cookie value, or an empty object if not found.
 */
const getCookie = (name) => {
  const cookieValue = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)')?.pop() || '';
  if (cookieValue) {
    const decodedValue = decodeURIComponent(cookieValue);

    try {
      const parsedValue = JSON.parse(decodedValue);

      // Only accept object-shaped preferences.
      if (parsedValue && typeof parsedValue === 'object' && !Array.isArray(parsedValue)) {
        return parsedValue;
      }

      return {};
    } catch {
      // Legacy versions stored plain strings like "closed".
      if (decodedValue === 'closed' || decodedValue === 'accepted' || decodedValue === 'true') {
        return { necessary: true };
      }

      return {};
    }
  }
  return {};
};

/**
 * Sets a cookie with the given name, value, and attributes.
 * @param {string} name - The name of the cookie.
 * @param {string} value - The value of the cookie.
 * @param {Object} attributes - The attributes for the cookie (e.g., expires, path, domain, sameSite, secure).
 */
const setCookie = (name, value, attributes) => {
  if (typeof attributes.expires === 'number') {
    attributes.expires = new Date(Date.now() + attributes.expires * 864e5);
  }

  if (attributes.expires) {
    attributes.expires = attributes.expires.toUTCString();
  }

  const stringifiedAttributes = Object.entries(attributes)
    .filter(([, value]) => value)
    .map(([key, value]) => `${key}${value === true ? '' : '=' + value.split(';')[0]}`)
    .join('; ');

  document.cookie = `${name}=${write(value)}; ${stringifiedAttributes}`;
};

/**
 * Manages the cookie consent banner with multiple settings.
 */
const CookieConsent = (() => {
  const cookieName = 'UA-CookiesConsent';
  const cookiesBanner = document.getElementById('ua-cookie-banner');
  const cookiesBannerBtn = document.getElementById('ua-cookie-banner-btn');
  const cookiePreferences = {
    necessary: true, // Always enabled
    functional: true,
    statistics: false,
    advertising: false,
  };

  /**
   * Initializes the cookie consent banner.
   * Shows the banner if the consent cookie is not set.
   * Adds event listeners to the accept button and checkboxes.
   */
  const init = () => {
    // Load saved preferences if they exist
    const savedPreferences = getCookie(cookieName);

    // Ensure savedPreferences is a valid JSON object
    if (Object.keys(savedPreferences).length > 0) {
      Object.assign(cookiePreferences, savedPreferences);
    } else {
      cookiesBanner.removeAttribute('hidden');
    }

    // Set initial states of checkboxes based on preferences
    document.querySelectorAll('.form-check-input').forEach((checkbox) => {
      const key = checkbox.getAttribute('id').replace('uaCookieInput', '').toLowerCase(); // Get the preference key
      if (key in cookiePreferences) {
        checkbox.checked = cookiePreferences[key];
      }
    });

    if (cookiesBannerBtn) {
      cookiesBannerBtn.addEventListener('click', acceptCookies);
    }
  };

  /**
   * Accepts the cookies by saving preferences and removing the banner.
   */
  const acceptCookies = () => {
    // Update preferences based on checkbox states
    document.querySelectorAll('.form-check-input').forEach((checkbox) => {
      const key = checkbox.getAttribute('id').replace('uaCookieInput', '').toLowerCase();
      if (key in cookiePreferences) {
        cookiePreferences[key] = checkbox.checked;
      }
    });

    const isSecure = window.location.protocol === 'https:';
    setCookie(cookieName, cookiePreferences, {
      expires: 90,
      path: '/',
      sameSite: isSecure ? 'None' : 'Lax', // Default to Lax for non-secure contexts
      secure: isSecure,
    });

    cookiesBanner.remove();
  };

  return { init };
})();

/**
 * Initializes the CookieConsent when the DOM content is loaded.
 */
document.addEventListener('DOMContentLoaded', CookieConsent.init);
